import { useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

export interface AppFormData {
  name: string;
  description: string;
  shopifyDashboardUrl: string;
  clientId: string;
  clientSecret: string;
  scopes: string;
}

export interface AppFormProps {
  formData: AppFormData;
  onChange: (data: AppFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string | null;
  mode: "create" | "edit";
  submitLabel: string;
  cancelHref?: string;
}

function SetupGuide() {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 sticky top-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">Setup Guide</h2>
      <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
        Complete steps 1-3 here, then continue setup on the app detail page.
      </p>

      <div className="space-y-5">
        {/* Step 1 */}
        <div>
          <p className="font-semibold text-gray-900 dark:text-dark-text mb-1">
            1. Create App in Shopify
          </p>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            Go to{" "}
            <a
              href="https://dev.shopify.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:text-primary-600 dark:text-primary-400"
            >
              dev.shopify.com/dashboard
            </a>{" "}
            &rarr; Apps &rarr; Create app
          </p>
        </div>

        {/* Step 2 */}
        <div>
          <p className="font-semibold text-gray-900 dark:text-dark-text mb-1">
            2. Get API Credentials
          </p>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            From Settings page, copy <span className="font-medium">Client ID</span> and{" "}
            <span className="font-medium">Client Secret</span>
          </p>
        </div>

        {/* Step 3 */}
        <div>
          <p className="font-semibold text-gray-900 dark:text-dark-text mb-1">
            3. Register App Here
          </p>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            Fill in the form with name, credentials, and scopes, then submit
          </p>
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-dark-border">
          <p className="text-sm text-gray-400 dark:text-dark-text-muted">
            After submitting, you&apos;ll continue setup on the app detail page.
          </p>
        </div>
      </div>
    </div>
  );
}

function FormCard({
  formData,
  onChange,
  onSubmit,
  loading,
  error,
  isCreate,
  submitLabel,
  cancelHref,
  scopesRef,
  resizeTextarea,
}: {
  formData: AppFormData;
  onChange: (data: AppFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string | null;
  isCreate: boolean;
  submitLabel: string;
  cancelHref?: string;
  scopesRef: React.RefObject<HTMLTextAreaElement | null>;
  resizeTextarea: () => void;
}) {
  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6">
      {error && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6"
        >
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            App Name
          </label>
          <input
            id="name"
            type="text"
            required
            autoFocus={isCreate}
            value={formData.name}
            onChange={(e) => onChange({ ...formData, name: e.target.value })}
            placeholder={isCreate ? "e.g., client-app-staging" : undefined}
            autoComplete="off"
            data-1p-ignore
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
          />
          {isCreate && (
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              A friendly name to identify this app
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            Description{" "}
            <span className="font-normal text-gray-400 dark:text-dark-text-muted">(optional)</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => onChange({ ...formData, description: e.target.value })}
            placeholder={isCreate ? "e.g., Staging app for client X product feed sync" : undefined}
            autoComplete="off"
            data-1p-ignore
            maxLength={500}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted resize-none"
          />
          {isCreate && (
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              What is this app used for?
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="shopifyDashboardUrl"
            className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            Shopify Dev Dashboard URL{" "}
            <span className="font-normal text-gray-400 dark:text-dark-text-muted">(optional)</span>
          </label>
          <input
            id="shopifyDashboardUrl"
            type="url"
            value={formData.shopifyDashboardUrl}
            onChange={(e) => onChange({ ...formData, shopifyDashboardUrl: e.target.value })}
            placeholder={
              isCreate ? "e.g., https://dev.shopify.com/dashboard/apps/12345" : undefined
            }
            autoComplete="off"
            data-1p-ignore
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
          />
          {isCreate && (
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              Link to this app in the Shopify Dev Dashboard
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="clientId"
            className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            Client ID
          </label>
          <input
            id="clientId"
            type="text"
            required
            value={formData.clientId}
            onChange={(e) => onChange({ ...formData, clientId: e.target.value })}
            placeholder={isCreate ? "e.g., abc123def456" : undefined}
            autoComplete="off"
            data-1p-ignore
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
          />
        </div>

        <div>
          <label
            htmlFor="clientSecret"
            className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2"
          >
            Client Secret
          </label>
          <input
            id="clientSecret"
            type="text"
            required={isCreate}
            value={formData.clientSecret}
            onChange={(e) => onChange({ ...formData, clientSecret: e.target.value })}
            placeholder={isCreate ? "shpss_xxxxx" : "Leave blank to keep existing"}
            autoComplete="off"
            data-1p-ignore
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
          />
          {!isCreate && (
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              Leave blank to keep the existing secret
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <label
              htmlFor="scopes"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary"
            >
              Scopes
            </label>
            <a
              href="https://shopify.dev/docs/api/usage/access-scopes"
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={-1}
              className="inline-flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              View all scopes
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <textarea
            ref={scopesRef}
            id="scopes"
            required
            value={formData.scopes}
            onChange={(e) => onChange({ ...formData, scopes: e.target.value })}
            onInput={resizeTextarea}
            placeholder={isCreate ? "e.g., read_products,write_products,read_orders" : undefined}
            autoComplete="off"
            data-1p-ignore
            rows={1}
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted resize-none overflow-hidden"
          />
          {isCreate && (
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
              Comma-separated list of scopes (copy from Shopify after configuring version)
            </p>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-dark-border flex justify-end gap-4">
          {cancelHref && (
            <Link
              to={cancelHref}
              className="px-6 py-2 bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
            >
              Cancel
            </Link>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AppForm({
  formData,
  onChange,
  onSubmit,
  loading,
  error,
  mode,
  submitLabel,
  cancelHref,
}: AppFormProps) {
  const isCreate = mode === "create";
  const scopesRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea to fit content
  const resizeTextarea = useCallback(() => {
    const textarea = scopesRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  // Resize on content change and initial render
  useEffect(() => {
    resizeTextarea();
  }, [formData.scopes, resizeTextarea]);

  // Side-by-side layout for create mode
  if (isCreate) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SetupGuide />
        <FormCard
          formData={formData}
          onChange={onChange}
          onSubmit={onSubmit}
          loading={loading}
          error={error}
          isCreate={isCreate}
          submitLabel={submitLabel}
          cancelHref={cancelHref}
          scopesRef={scopesRef}
          resizeTextarea={resizeTextarea}
        />
      </div>
    );
  }

  // Single column for edit mode
  return (
    <FormCard
      formData={formData}
      onChange={onChange}
      onSubmit={onSubmit}
      loading={loading}
      error={error}
      isCreate={isCreate}
      submitLabel={submitLabel}
      cancelHref={cancelHref}
      scopesRef={scopesRef}
      resizeTextarea={resizeTextarea}
    />
  );
}
